import type { RefObject } from "react";
import type { WitnessCardData } from "@/components/ui/Cwitnesscard";
import {
  CWITNESS_PARALLAX_TEXT_DESKTOP_DEFAULT_BOTTOM_SPACING,
  CWITNESS_PARALLAX_TEXT_DESKTOP_DEFAULT_TOP_SPACING,
  CWITNESS_PARALLAX_TEXT_MOBILE_DEFAULT_BOTTOM_SPACING,
  CWITNESS_PARALLAX_TEXT_MOBILE_DEFAULT_TOP_SPACING,
} from "./constants";
import type {
  HeroPresentationModel,
  ParallaxTextSpacing,
  ParallaxTextSpacingValue,
} from "./types";

interface MobileFlagInput {
  isMobile: boolean;
}

interface ShouldRenderWitnessContentInput {
  isSectionInView: boolean;
  selectedCard: WitnessCardData | null;
}

interface ResolveHydratedRightColumnRefInput {
  rightColumnElement: HTMLDivElement | null;
  shouldRenderContent: boolean;
}

interface ResolveParallaxTextSpacingInput {
  isMobile: boolean;
  textBottomSpacing: ParallaxTextSpacingValue | undefined;
  textTopSpacing: ParallaxTextSpacingValue | undefined;
}

interface ResolveSpacingValueInput {
  defaultValue: ParallaxTextSpacingValue;
  value: ParallaxTextSpacingValue | undefined;
}

export function getParallaxTextSize({ isMobile }: MobileFlagInput): number {
  return isMobile ? 18 : 20;
}

export function resolveHeroPresentation({
  isMobile,
}: MobileFlagInput): HeroPresentationModel {
  return isMobile
    ? {
        alignItems: "flex-end",
        flexDirection: "row",
        gap: 15,
        justifyContent: "center",
        subtitleSize: 38,
        textAlign: "center",
        titleSize: 38,
      }
    : {
        alignItems: "flex-start",
        flexDirection: "column",
        gap: undefined,
        justifyContent: "center",
        subtitleSize: 69,
        textAlign: "left",
        titleSize: 68,
      };
}

export function shouldRenderWitnessContent({
  isSectionInView,
  selectedCard,
}: ShouldRenderWitnessContentInput): boolean {
  return isSectionInView || selectedCard != null;
}

export function resolveHydratedRightColumnRef({
  rightColumnElement,
  shouldRenderContent,
}: ResolveHydratedRightColumnRefInput):
  | RefObject<HTMLDivElement | null>
  | undefined {
  if (!shouldRenderContent || !rightColumnElement) {
    return undefined;
  }

  return { current: rightColumnElement };
}

export function resolveParallaxTextSpacing({
  isMobile,
  textBottomSpacing,
  textTopSpacing,
}: ResolveParallaxTextSpacingInput): ParallaxTextSpacing {
  const defaultTextTopSpacing = isMobile
    ? CWITNESS_PARALLAX_TEXT_MOBILE_DEFAULT_TOP_SPACING
    : CWITNESS_PARALLAX_TEXT_DESKTOP_DEFAULT_TOP_SPACING;
  const defaultTextBottomSpacing = isMobile
    ? CWITNESS_PARALLAX_TEXT_MOBILE_DEFAULT_BOTTOM_SPACING
    : CWITNESS_PARALLAX_TEXT_DESKTOP_DEFAULT_BOTTOM_SPACING;

  return {
    textTopSpacing: resolveSpacingValue({
      defaultValue: defaultTextTopSpacing,
      value: textTopSpacing,
    }),
    textBottomSpacing: resolveSpacingValue({
      defaultValue: defaultTextBottomSpacing,
      value: textBottomSpacing,
    }),
  };
}

function resolveSpacingValue({
  defaultValue,
  value,
}: ResolveSpacingValueInput): ParallaxTextSpacingValue {
  return value ?? defaultValue;
}
